import React, { useContext, useEffect, useState } from "react";
import ReactDatePicker, { setDefaultLocale } from "react-datepicker";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { getDomainConfig } from "../../config/config";
import { DepDateContext } from "../../contexts/dep-date-context";
import { useCart } from "../../hooks/useCart";
import { getLanguageCode } from "../../utils/getURLParams";
import { logModalAction } from "../Analytics/Universal/PageView/datalayer.functions";
import StandardFieldset from "../UI/StandardFieldset";
import StandardFormItem from "../UI/StandardFormItem";
import StandardH2 from "../UI/StandardH2";
import StandardInfo from "../UI/StandardInfo";
import StandardInputWrapper from "../UI/StandardInputWrapper";
import StandardLabel from "../UI/StandardLabel";
import StandardValidation from "../UI/StandardValidation";
import { googleDataLayer } from "../Analytics/Universal/PageView/googleDataLayer";
import { format } from "date-fns";
import { ABContext } from "../../contexts/ab-context";

const DepartureDate = (props) => {
  const config = getDomainConfig();
  const { t } = useTranslation("common");

  /* LOADING THE BRANDED SCSS */
  const [styles, setStyles] = useState(false);

  const { isSuccess: cartIsSuccess, data: cartContents } = useCart();

  useEffect(() => {
    const getStyles = async (theFileName, theFileEnd) => {
      if (config.brand) {
        let moduleStyles = false;
        try {
          moduleStyles = await import(
            "./" + theFileName + config.brand + theFileEnd
          );
          setStyles(moduleStyles);
        } catch (e) {
          console.error(
            `Styles for ${theFileName} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles("DepartureDate", ".module.scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  // Loading import for calendar translation other than English
  useEffect(() => {
    // Getting language prefix from URL
    const domainLang = getLanguageCode(config.brand);
    // Import function to load translation from date-fns library
    const importLocaleFile = async (domainLang) => {
      let calendarLang;
      switch (domainLang) {
        case "de":
          calendarLang = "de";
          break;
        default:
          calendarLang = "en";
      }
      // This webpack option stops all of the date-fns files being imported and chunked.
      if (calendarLang !== 'en'){
        const localeToSet = await import(`date-fns/locale/${calendarLang}`);
        // Setting imported language for the Calendar
        setDefaultLocale(localeToSet.default);
      }
    };
    // // If the language is other than English, load import for given language
    if (domainLang && !domainLang.includes("en")) {
      try {
        importLocaleFile(domainLang);
      } catch (err) {
        console.error(err);
      }
    }
  }, [config.brand]);

  /* LOADING THE BRANDED SCSS */
  const [styles2, setStyles2] = useState(false);

  useEffect(() => {
    const getStyles2 = async (theFileName2, theFileEnd2) => {
      if (config.brand) {
        let moduleStyles2 = false;
        try {
          moduleStyles2 = await import(
            "./" + theFileName2 + config.brand + theFileEnd2
          );
          setStyles2(moduleStyles2);
        } catch (e) {
          console.error(
            `Styles for ${theFileName2} could not be loaded, ${e.message}`
          );
        }
      }
    };

    // Specify file name and end of file name here.
    getStyles2("ReactDatepicker", ".scss");
  }, [config.brand]);

  /* END LOADING THE BRANDED SCSS */

  // CRO-1381-checkout-redesign
  const abContext = useContext(ABContext);
  const [isCro1381, setIsCro1381] = useState(false);

  useEffect(() => {
    if ((config.brand !== 'Sw') && abContext.cro1381 !== undefined) setIsCro1381(abContext.cro1381.variant);
  }, [abContext]);
  // END CRO-1381-checkout-redesign

  // Setting Reactdatepicker's "openToDate" based on ticket year
  const [openToDateYear, setOpenToDateYear] = useState(null);

  useEffect(() => {
    if (cartIsSuccess && cartContents.data.tickets) {
      const currentYear = new Date().getFullYear();
      const ticketYears = cartContents.data.tickets
        .map(ticket => ticket.year)
        .filter(Boolean)
        .map(Number);

      const hasCurrentYearTicket = ticketYears.includes(currentYear);

      if (hasCurrentYearTicket) {
        setOpenToDateYear(new Date());
      } else {
        const futureYears = ticketYears.filter(year => year > currentYear);
        if (futureYears.length > 0) {
          const minFutureYear = Math.min(...futureYears);
          setOpenToDateYear(new Date(`${minFutureYear}-01-01`));
        } else {
          setOpenToDateYear(new Date());
        }
      }
    } else {
      setOpenToDateYear(new Date());
    }
  }, [cartContents, cartIsSuccess]);
  // Setting Reactdatepicker's "openToDate" based on ticket year END


  const { depDate, setDepDate } = useContext(DepDateContext);

  const handleDepartureDateChange = (event) => {
    // Revalidate as needed.
    props.trigger("departure_date");
    setDepDate(event);
    googleDataLayer.createDepartureDateEvent(format(new Date(event), 'yyyy-MM-dd'))
  };

  const classes = styles.textDateInput;
  const [showError, setShowError] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [isCalendarOpened, setIsCalendarOpened] = useState(false);

  let ticketsDates = [];

  if (cartIsSuccess) {
    cartContents.data.tickets.forEach((ticket) => {
      let ticketYear;
      if (ticket.year && ticket.year !== "") {
        ticketYear = Number(ticket.year) + 1;
        ticketYear = ticketYear.toString();
        ticketsDates.push(new Date(ticketYear));
      }
    });
  }

  useEffect(() => {
    if (props.errors.departure_date) {
      setShowError(true);
      if (isCalendarOpened) {
        setShowError(false);
      }
    } else {
      setShowError(false);
    }
  },[props, isCalendarOpened])

  return (
    <StandardFieldset id="departure_date-pane">
      <StandardH2 className={isCro1381 && ("cro1381H2")}>{t("when-you-are-traveling")}</StandardH2>
      <StandardFormItem
        className="textItem checkoutForm__date"
        id="departure_date_form_item"
      >
        <StandardLabel htmlFor="departure_date" required="true">
          {t("departure-date")}
        </StandardLabel>
        <StandardInputWrapper className="textWrapper">
          <Controller
            control={props.control}
            name="departure_date"
            {...props.register("departure_date", { required: true })}
            render={({ field: { onChange, onBlur, value, ref } }) => (
              <ReactDatePicker
                dateFormat="dd MMM yyyy"
                ref={(e) => {e && ref(e.input)}}
                onChange={(e) => {
                  onChange(e);
                  handleDepartureDateChange(e);
                }}
                onBlur={onBlur}
                selected={value}
                minDate={new Date()}
                maxDate={ticketsDates.length > 0 && ticketsDates[0] - 1}
                className={classes}
                id="departure_date"
                onCalendarOpen={() => {
                  setIsCalendarOpened(true);
                  if (config.siteName === "Universal Bookings") {
                    logModalAction(
                      "Modal Open",
                      "Checkout Calendar",
                      "Calendar Open"
                    );
                  }
                }}
                onCalendarClose={() => {
                  setIsCalendarOpened(false);
                  if (!depDate) {
                    setShowError(true);
                  } else {
                    setShowError(false);
                    setShowSuccess(true);
                  }
                  if (config.siteName === "Universal Bookings") {
                    logModalAction(
                      "Modal Close",
                      "Checkout Calendar",
                      "Calendar Closed"
                    );
                  }
                }}
                openToDate={openToDateYear}
                {...(isCro1381 && {
                  popperPlacement: "bottom-start",
                  popperModifiers: [
                    {
                      name: "flip",
                      options: {
                        fallbackPlacements: ["bottom-start"],
                        allowedAutoPlacements: ["bottom-start"],
                      },
                    },
                  ],
                  showPopperArrow: false,
                })}
                customInput={
                  <input inputmode="none" autocomplete="off"/>
                }
              />
              )}
            />

          {showSuccess && (
            <StandardValidation
              valType="success"
              icon="true"
              className="input"
            ></StandardValidation>
          )}
          {showError && (
            <StandardValidation className="input" valType="error" icon="true">
              {t("please-enter-a-valid-departure-date")}
            </StandardValidation>
          )}

          <StandardInfo>
            {config.brand === "Uo"
              ? ""
              : t("so-that-we-can-offer-you-the-relevant-payment-options")}
          </StandardInfo>
        </StandardInputWrapper>
      </StandardFormItem>
    </StandardFieldset>
  );
};

export default DepartureDate;
